import { useModal } from "../../../../../hooks/useModal.js";
import { Modal } from "../../../../../components/modal.jsx";
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as zod from "zod";
import { SimpleField } from "../../../../../components/fields/simpleField";
import { Button } from "../../../../../components/button";
import { useInternet } from "../../../../../hooks/useInternet";
import { SubmitButton } from "../../../../../components/fields/submitButton";

export const GENERATE_DOCUMENT_MODAL = "generate_document_modal";

export function GenerateDocumentModal({ onUpload, document }) {
  let { openModal, checkModal, closeModal } = useModal();

  let { formState, handleSubmit, control, watch, setValue } = useForm({
    resolver: zodResolver(
      zod
        .object({
          lines: zod.array(
            zod.object({
              quantity: zod.number().nullish(),
              name: zod.string().nullish(),
              unit: zod.number().nullish(),
            })
          ),
          taxes: zod.array(
            zod.object({
              name: zod.string().nullish(),
              percentage: zod.number().nullish(),
            })
          ),
          notes: zod.string().nullish(),
        })
        .nullish()
    ),
    defaultValues: {
      lines: [{ quantity: null, name: null, unit: null }],
      taxes: [{ name: null, percent: null }],
      notes: window.localStorage.defaultNotes,
    },
  });
  let internet = useInternet();
  let generatePdf = (v) => {
    window.localStorage.defaultNotes = v.notes;
    let subtotal = v.lines
      .map(({ unit, quantity }) => unit * quantity)
      .reduce((a, b) => a + b, 0);

    for (let tax of v.taxes) {
      tax.amount = (subtotal * tax.percentage) / 100;
    }

    let total =
      subtotal + v.taxes.map(({ amount }) => amount).reduce((a, b) => a + b, 0);
    return internet
      .post("/documents/generate_pdf", {
        document,
        subtotal,
        total,
        ...v,
      })
      .then(async (res) => {
        let upload = await res.json();
        onUpload(upload.id);
      });
  };

  return (
    <Modal onClose={() => closeModal(GENERATE_DOCUMENT_MODAL)} fullPage={true}>
      <div className={"flex flex-col gap-3"}>
        <h2 className={"font-medium"}>Generate document</h2>
        <form
          onSubmit={handleSubmit((v) => generatePdf(v))}
          className={"flex flex-col gap-3"}
        >
          {watch("lines").map((line, i) => (
            <div className={"flex gap-3"}>
              <SimpleField
                name={`lines.${i}.quantity`}
                type={"number"}
                label={i == 0 && "Quantity"}
                control={control}
              />
              <div className={"flex-grow"}>
                <SimpleField
                  name={`lines.${i}.name`}
                  label={i == 0 && "Name"}
                  control={control}
                />
              </div>
              <SimpleField
                name={`lines.${i}.unit`}
                type={"number"}
                label={i == 0 && "Unit price"}
                control={control}
              />
              <div className={"flex items-end"}>
                <Button
                  onClick={() =>
                    setValue(
                      "lines",
                      watch("lines").filter((l, j) => j != i)
                    )
                  }
                >
                  <span className={"fa fa-times"} />{" "}
                </Button>
              </div>
            </div>
          ))}
          <Button
            onClick={() =>
              setValue(
                "lines",
                watch("lines").concat([
                  { quantity: null, name: null, unit: null },
                ])
              )
            }
          >
            Add line
          </Button>

          <div className={"flex items-end flex-col gap-3"}>
            {watch("taxes").map((_, i) => {
              return (
                <div className={"flex gap-3 items-center"}>
                  <div style={{ width: 200 }}>
                    <SimpleField
                      name={"taxes." + i + ".name"}
                      type={"number"}
                      placeholder={"Tax"}
                      control={control}
                    />
                  </div>
                  <div style={{ width: 100 }}>
                    <SimpleField
                      name={"taxes." + i + ".percentage"}
                      type={"number"}
                      placeholder={"%"}
                      control={control}
                    />
                  </div>
                  <Button
                    onClick={() =>
                      setValue(
                        "taxes",
                        watch("taxes").filter((_, k) => k != i)
                      )
                    }
                  >
                    <span className={"fa fa-times"} />{" "}
                  </Button>
                </div>
              );
            })}
            <Button
              sizing={"s"}
              className={"px-3"}
              onClick={() =>
                setValue("taxes", [
                  ...watch("taxes"),
                  { name: null, percentage: null },
                ])
              }
            >
              Add tax
            </Button>
          </div>
          <div className={"flex"} style={{ width: 400, maxWidth: "100%" }}>
            <SimpleField
              label={"Notes"}
              name={"notes"}
              type={"textarea"}
              control={control}
              wrapperClasses={"w-full"}
            />
          </div>
          <div className={"flex"}>
            <SubmitButton formState={formState}>Generate PDF</SubmitButton>
          </div>
        </form>
      </div>
    </Modal>
  );
}
